import { useCallback, useEffect, useMemo, useState } from 'react';
import { Gauge, Network, Table2 } from 'lucide-react';
import { getNetworkUsage } from '../../api/api';
import { formatRelativeTime, formatTimestamp } from '../../utils/dateUtils';
import {
  MAX_DAILY_DAYS,
  PERIOD_PRESETS,
  autoGranularity,
  daysInRange,
  formatBytes,
  formatBytesCompact,
  formatDateRange,
  formatPeriod,
  formatRate,
  formatShare,
  niceCeiling,
  presetRange,
  splitUsage,
} from '../../utils/networkUsageUtils';
import useIsMobile from '../../utils/useIsMobile';
import styles from './NetworkUsagePanel.module.scss';

// The hub reports every 3 minutes; past this the current rate is out of date.
const STALE_AFTER_SECONDS = 10 * 60;
const MAX_AXIS_LABELS = 8;
const MAX_AXIS_LABELS_MOBILE = 4;
const DEFAULT_PRESET = 'thisMonth';

const SERIES = [
  { id: 'ntrip', label: 'NTRIP', color: '#168447' },
  { id: 'others', label: 'Others', color: '#5b8def' },
];

const COLUMNS = [
  { id: 'total', label: 'Total' },
  { id: 'ntrip', label: 'NTRIP' },
  { id: 'others', label: 'Others' },
];

function RxTx({ usage }) {
  return (
    <small className={styles.rxTx}>
      ↓ {formatBytes(usage.rx)} · ↑ {formatBytes(usage.tx)}
    </small>
  );
}

// The rate over the hub's latest report, which covers roughly one sync interval.
function LiveRate({ latestReport }) {
  const intervalSeconds = latestReport?.interval_s || 0;
  const isStale =
    !latestReport || Date.now() / 1000 - latestReport.received_at > STALE_AFTER_SECONDS;
  const current = latestReport && intervalSeconds > 0 ? splitUsage(latestReport) : null;

  return (
    <div className={`${styles.live} ${isStale ? styles.stale : ''}`}>
      <span className={styles.liveLabel}>
        <i />
        Live
      </span>
      {COLUMNS.map(column => (
        <span key={column.id} className={styles.liveValue}>
          <small>{column.label}</small>
          <strong>
            {current ? formatRate((current[column.id].sum / intervalSeconds) * 60) : '–'}
          </strong>
        </span>
      ))}
      <span className={styles.liveNote}>
        {latestReport ? (
          <>
            Over the last {Math.round(intervalSeconds / 60) || '<1'} min, reported{' '}
            <span title={formatTimestamp(latestReport.received_at)}>
              {formatRelativeTime(latestReport.received_at)}
            </span>
            {isStale && ' · out of date'}
          </>
        ) : (
          'No usage report from the hub yet'
        )}
      </span>
    </div>
  );
}

function PeriodTiles({ usage, days }) {
  return (
    <div className={styles.tiles}>
      <div className={`${styles.tile} ${styles.tileTotal}`}>
        <span className={styles.tileLabel}>Total</span>
        <strong>{formatBytes(usage.total.sum)}</strong>
        <RxTx usage={usage.total} />
        {days > 1 && (
          <small className={styles.tileAverage}>
            ≈ {formatBytes(usage.total.sum / days)} per day
          </small>
        )}
      </div>
      {SERIES.map(series => (
        <div key={series.id} className={styles.tile}>
          <span className={styles.tileLabel}>
            <i style={{ backgroundColor: series.color }} />
            {series.label}
          </span>
          <strong>
            {formatBytes(usage[series.id].sum)}
            <em>{formatShare(usage[series.id].sum, usage.total.sum)}</em>
          </strong>
          <RxTx usage={usage[series.id]} />
        </div>
      ))}
    </div>
  );
}

// Centered over the bar, but kept inside the plot near either edge.
const tooltipPosition = (index, count) => {
  const fraction = (index + 0.5) / count;
  const left = `${fraction * 100}%`;
  if (fraction < 0.2) {
    return { left, transform: 'translateX(-10%)' };
  }
  if (fraction > 0.8) {
    return { left, transform: 'translateX(-90%)' };
  }
  return { left };
};

function UsageChart({ buckets, granularity }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const isMobile = useIsMobile();

  const bars = useMemo(
    () => buckets.map(bucket => ({ period: bucket.period, usage: splitUsage(bucket) })),
    [buckets]
  );
  const axisMax = niceCeiling(Math.max(0, ...bars.map(bar => bar.usage.total.sum)));
  const labelStep = Math.ceil(bars.length / (isMobile ? MAX_AXIS_LABELS_MOBILE : MAX_AXIS_LABELS));
  const active = activeIndex === null ? null : bars[activeIndex];

  return (
    <div className={styles.chart}>
      <div className={styles.plot}>
        <div className={styles.yAxis} aria-hidden="true">
          <span>{formatBytesCompact(axisMax)}</span>
          <span>{formatBytesCompact(axisMax / 2)}</span>
          <span>0</span>
        </div>
        <div className={styles.plotArea} onMouseLeave={() => setActiveIndex(null)}>
          <div className={styles.gridLines} aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
          <div className={styles.bars}>
            {bars.map((bar, index) => {
              const ntripHeight = (bar.usage.ntrip.sum / axisMax) * 100;
              const othersHeight = (bar.usage.others.sum / axisMax) * 100;
              return (
                <button
                  key={bar.period}
                  type="button"
                  className={`${styles.barSlot} ${activeIndex === index ? styles.barActive : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onBlur={() => setActiveIndex(null)}
                  aria-label={`${formatPeriod(bar.period, granularity)}: total ${formatBytes(
                    bar.usage.total.sum
                  )}, NTRIP ${formatBytes(bar.usage.ntrip.sum)}, others ${formatBytes(
                    bar.usage.others.sum
                  )}`}
                >
                  <span className={styles.barStack}>
                    {othersHeight > 0 && (
                      <i style={{ height: `${othersHeight}%`, backgroundColor: SERIES[1].color }} />
                    )}
                    {ntripHeight > 0 && (
                      <i style={{ height: `${ntripHeight}%`, backgroundColor: SERIES[0].color }} />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          {active && (
            <div
              className={styles.tooltip}
              style={tooltipPosition(activeIndex, bars.length)}
              role="status"
            >
              <span className={styles.tooltipTitle}>
                {formatPeriod(active.period, granularity)}
              </span>
              <span>
                <strong>{formatBytes(active.usage.total.sum)}</strong> Total
              </span>
              {SERIES.map(series => (
                <span key={series.id}>
                  <i style={{ backgroundColor: series.color }} />
                  <strong>{formatBytes(active.usage[series.id].sum)}</strong> {series.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.xAxis} aria-hidden="true">
        {bars.map((bar, index) => (
          <span key={bar.period}>
            {index % labelStep === 0 ? formatPeriod(bar.period, granularity) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function BucketTable({ buckets, granularity }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.bucketTable}>
        <thead>
          <tr>
            <th>{granularity === 'month' ? 'Month' : 'Day'}</th>
            {COLUMNS.map(column => (
              <th key={column.id}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...buckets].reverse().map(bucket => {
            const usage = splitUsage(bucket);
            return (
              <tr key={bucket.period}>
                <th scope="row">{bucket.period}</th>
                {COLUMNS.map(column => (
                  <td key={column.id}>
                    <strong>{formatBytes(usage[column.id].sum)}</strong>
                    <RxTx usage={usage[column.id]} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CustomRangeForm({ draft, onChange, onApply }) {
  const days = draft.startDate && draft.endDate ? daysInRange(draft.startDate, draft.endDate) : 0;
  const invalid = !draft.startDate || !draft.endDate || days < 1;
  const dailyAllowed = !invalid && days <= MAX_DAILY_DAYS;

  // Changing a date re-picks the bucket size; the user can still override it.
  const setDate = (field, value) =>
    onChange(current => {
      const next = { ...current, [field]: value };
      const nextDays =
        next.startDate && next.endDate ? daysInRange(next.startDate, next.endDate) : 0;
      return nextDays >= 1 ? { ...next, granularity: autoGranularity(nextDays) } : next;
    });

  return (
    <div className={styles.customRange}>
      <label>
        <span>From</span>
        <input
          type="date"
          value={draft.startDate}
          onChange={event => setDate('startDate', event.target.value)}
        />
      </label>
      <label>
        <span>To</span>
        <input
          type="date"
          value={draft.endDate}
          onChange={event => setDate('endDate', event.target.value)}
        />
      </label>
      <label>
        <span>Group by</span>
        <select
          value={dailyAllowed ? draft.granularity : 'month'}
          onChange={event => onChange(current => ({ ...current, granularity: event.target.value }))}
        >
          <option value="day" disabled={!dailyAllowed}>
            Day
          </option>
          <option value="month">Month</option>
        </select>
      </label>
      <button
        type="button"
        className={styles.applyButton}
        onClick={() =>
          onApply({ ...draft, granularity: dailyAllowed ? draft.granularity : 'month' })
        }
        disabled={invalid}
      >
        Apply
      </button>
      {invalid && draft.startDate && draft.endDate && (
        <small className={styles.hint}>The start date must be on or before the end date.</small>
      )}
    </div>
  );
}

function NetworkUsagePanel({ serialNumber, refreshToken }) {
  const [presetId, setPresetId] = useState(DEFAULT_PRESET);
  const [range, setRange] = useState(() => presetRange(DEFAULT_PRESET));
  const [draftRange, setDraftRange] = useState(range);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTable, setShowTable] = useState(false);

  const fetchUsage = useCallback(async () => {
    if (!serialNumber) {
      return;
    }
    setLoading(true);
    try {
      const response = await getNetworkUsage(serialNumber, range);
      setData(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 404) {
        setData(null);
        setError('');
      } else {
        setError(err.response?.data?.detail || 'Failed to load network usage');
      }
    } finally {
      setLoading(false);
    }
  }, [serialNumber, range]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage, refreshToken]);

  // "All time" starts on the hub's first reported day, which only the
  // response tells us; re-align once it is known.
  const firstDay = data?.all_time?.first_day || null;
  useEffect(() => {
    if (presetId === 'allTime' && firstDay) {
      const aligned = presetRange('allTime', firstDay);
      setRange(current => (current.startDate === aligned.startDate ? current : aligned));
    }
  }, [presetId, firstDay]);

  const selectPreset = id => {
    setPresetId(id);
    if (id !== 'custom') {
      const next = presetRange(id, firstDay);
      setRange(next);
      setDraftRange(next);
    }
  };

  const applyCustomRange = next => {
    setDraftRange(next);
    setRange(next);
  };

  const period = data?.range;
  const periodUsage = period ? splitUsage(period) : null;
  const periodDays = period ? daysInRange(period.start_date, period.end_date) : 0;
  const periodLabel =
    presetId === 'custom'
      ? 'Custom period'
      : PERIOD_PRESETS.find(preset => preset.id === presetId)?.label;
  const hasBreakdown = period && period.buckets.length > 1;

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <Gauge size={17} />
          <span>
            <strong>Network usage</strong>
            <small>Cellular modem traffic · days and months are UTC</small>
          </span>
        </div>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {!data && !loading && !error && (
        <div className={styles.emptyState}>
          <Network size={26} />
          <strong>No network usage data</strong>
          <p>The hub has not reported any network usage.</p>
        </div>
      )}

      {data && <LiveRate latestReport={data.latest_report} />}

      {(data || loading) && (
        <div className={styles.periodSection}>
          <div className={styles.presets} role="group" aria-label="Period">
            {[...PERIOD_PRESETS, { id: 'custom', label: 'Custom' }].map(preset => (
              <button
                key={preset.id}
                type="button"
                className={presetId === preset.id ? styles.presetActive : ''}
                onClick={() => selectPreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {presetId === 'custom' && (
            <CustomRangeForm
              draft={draftRange}
              onChange={setDraftRange}
              onApply={applyCustomRange}
            />
          )}

          {period ? (
            <div className={loading ? styles.loading : ''}>
              <div className={styles.periodHeading}>
                <strong>{periodLabel}</strong>
                <span>
                  {formatDateRange(period.start_date, period.end_date)}
                  {periodDays > 1 && ` · ${periodDays} days`}
                </span>
              </div>
              <PeriodTiles usage={periodUsage} days={periodDays} />

              {hasBreakdown ? (
                <>
                  <div className={styles.chartHeader}>
                    <div className={styles.legend}>
                      <strong>{period.granularity === 'month' ? 'By month' : 'By day'}</strong>
                      {SERIES.map(series => (
                        <span key={series.id}>
                          <i style={{ backgroundColor: series.color }} />
                          {series.label}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={`${styles.tableToggle} ${showTable ? styles.toggleActive : ''}`}
                      onClick={() => setShowTable(value => !value)}
                      aria-pressed={showTable}
                    >
                      <Table2 size={14} />
                      Table
                    </button>
                  </div>
                  <UsageChart buckets={period.buckets} granularity={period.granularity} />
                  {showTable && (
                    <BucketTable buckets={period.buckets} granularity={period.granularity} />
                  )}
                </>
              ) : (
                <p className={styles.singleDayNote}>
                  Pick a longer period to see the day-by-day breakdown.
                </p>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Network size={24} />
              <strong>Loading…</strong>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default NetworkUsagePanel;
